/**
 * usePlaidAccounts — Listen to account-level Plaid data
 *
 * Reads from plaid_accounts subcollection (populated by Cloud Function during sync).
 * Returns accounts grouped by subtype: checking, savings, credit.
 *
 * Firestore schema (per account doc):
 *   accountId: string (Plaid account_id, used as doc ID)
 *   itemId: string (parent Plaid item)
 *   name: string (account display name)
 *   mask: string (last 4 digits)
 *   subtype: 'checking' | 'savings' | 'credit'
 *   balanceCents: number (current balance in cents)
 *   institutionName: string
 *   plaidLogo: string | null (base64 PNG)
 *   plaidColor: string | null (hex)
 *   lastSyncAt: number (Unix ms)
 *
 * TODO: Cloud Function must populate this subcollection during Plaid sync.
 * See: functions/src/plaid/syncAccounts.ts (to be created)
 */

import { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';
import type { PlaidAccount } from '@/components/money/AccountsAccordion';

interface UsePlaidAccountsResult {
  accounts: PlaidAccount[];
  isLoading: boolean;
  /** Most recent sync timestamp across all accounts */
  lastSyncAt: number | null;
}

export function usePlaidAccounts(userId: string | undefined): UsePlaidAccountsResult {
  const [accounts, setAccounts] = useState<PlaidAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setAccounts([]);
      setIsLoading(false);
      return;
    }

    const colRef = collection(db, 'users', userId, 'plaid_accounts');
    const unsub = onSnapshot(
      colRef,
      (snap) => {
        const accts: PlaidAccount[] = [];
        for (const doc of snap.docs) {
          const data = doc.data();
          accts.push({
            id: doc.id,
            name: data.name || 'Account',
            mask: data.mask || '····',
            subtype: data.subtype || 'checking',
            balanceCents: data.balanceCents ?? 0,
            institutionName: data.institutionName || 'Bank',
            plaidLogo: data.plaidLogo || null,
            plaidColor: data.plaidColor || null,
            status: data.status || null,
          });
        }
        setAccounts(accts);
        setIsLoading(false);
      },
      (err) => {
        console.error('[usePlaidAccounts] Error:', err);
        setIsLoading(false);
      }
    );

    return unsub;
  }, [userId]);

  const lastSyncAt = accounts.length > 0
    ? Math.max(...accounts.map(() => Date.now())) // TODO: read from doc data.lastSyncAt
    : null;

  return { accounts, isLoading, lastSyncAt };
}
